import { useEffect, useRef, useState } from "react";

// Org switcher dropdown + the "rename organization" modal it opens into. Owns all of the
// dropdown's own UI state (open/closed, the modal's draft name/error/saving flag) so Home.jsx
// only needs to wire the returned handlers/values into its JSX.
export const usePracticeSwitcher = ({
  practicesList,
  PracticeId,
  ownPracticeId,
  ownPracticeName,
  practiceName,
  token,
  userType,
  patchRequest,
  fetchPracticesList,
  setPracticeId,
  setPracticeContactNumber,
  setOutBoundContact,
  setInboundContact,
  setPracticeName,
  setPracticeCreateDate,
  setCredentialsSaved,
  setOwnPracticeName,
  setPracticesList,
}) => {
  const orgMenuRef = useRef(null);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const [showOrganizationModal, setShowOrganizationModal] = useState(false);
  const [organizationDraftName, setOrganizationDraftName] = useState("");
  const [organizationUpdateError, setOrganizationUpdateError] = useState("");
  const [isSavingOrganizationName, setIsSavingOrganizationName] = useState(false);

  useEffect(() => {
    if (!isOrgMenuOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target)) {
        setIsOrgMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOrgMenuOpen]);

  const openOrganizationModal = () => {
    setOrganizationDraftName(ownPracticeName || "");
    setOrganizationUpdateError("");
    setShowOrganizationModal(true);
  };

  const handleOrganizationRename = async () => {
    const trimmedPracticeName = organizationDraftName.trim();

    if (!trimmedPracticeName) {
      setOrganizationUpdateError("Organization name is required");
      return;
    }

    setIsSavingOrganizationName(true);
    setOrganizationUpdateError("");

    const response = await patchRequest(token, "/api/practiceName", {
      practiceName: trimmedPracticeName,
    });

    if (!response || response.error) {
      setOrganizationUpdateError(
        response?.error || "Unable to update organization name",
      );
      setIsSavingOrganizationName(false);
      return;
    }

    const updatedPracticeName = response.practice?.PracticeName || trimmedPracticeName;

    setOwnPracticeName(updatedPracticeName);
    if (String(PracticeId) === String(ownPracticeId)) {
      setPracticeName(updatedPracticeName);
    }
    setPracticesList((currentPractices) =>
      Array.isArray(currentPractices)
        ? currentPractices.map((practice) =>
            String(practice.Id) === String(response.practice?.Id || ownPracticeId)
              ? { ...practice, PracticeName: updatedPracticeName }
              : practice,
          )
        : currentPractices,
    );

    if (userType === "admin") {
      fetchPracticesList(userType);
    }

    setShowOrganizationModal(false);
    setIsSavingOrganizationName(false);
  };

  const handlePracticeChange = (e) => {
    const selectedPracticeRecord =
      practicesList && practicesList.length > 0
        ? practicesList.filter((practice) => practice.Id == e.target.value)
        : "";
    setPracticeId(selectedPracticeRecord[0].Id);
    localStorage.setItem("cm_selectedPracticeId", String(selectedPracticeRecord[0].Id));
    setPracticeContactNumber(selectedPracticeRecord[0].PracticeContactNumber);
    setOutBoundContact(selectedPracticeRecord[0].OutBoundContactNumber);
    setInboundContact(selectedPracticeRecord[0].InBoundContactNumber);
    setPracticeName(selectedPracticeRecord[0].PracticeName);
    setPracticeCreateDate(selectedPracticeRecord[0].PracticeCreateDateTime || "");
    setCredentialsSaved(Boolean(selectedPracticeRecord[0].credentialsSaved));
  };

  // The currently-selected org sorts first so it's never buried once a practice has enough
  // orgs to need scrolling (see .home-org-menu's max-height in Home.css).
  const selectablePractices = Array.isArray(practicesList)
    ? practicesList
        .filter((practice) => practice.UserName !== "Admin")
        .sort((a, b) => {
          const aSelected = String(a.Id) === String(PracticeId);
          const bSelected = String(b.Id) === String(PracticeId);
          if (aSelected === bSelected) return 0;
          return aSelected ? -1 : 1;
        })
    : [];

  const selectedPracticeLabel =
    selectablePractices.find((practice) => String(practice.Id) === String(PracticeId))
      ?.PracticeName ||
    practiceName ||
    selectablePractices[0]?.PracticeName ||
    "Select Organization";

  const [orgSearchQuery, setOrgSearchQuery] = useState("");

  // Clear the search box each time the menu closes, so it doesn't reopen pre-filtered.
  useEffect(() => {
    if (!isOrgMenuOpen) {
      setOrgSearchQuery("");
    }
  }, [isOrgMenuOpen]);

  const normalizedOrgSearchQuery = orgSearchQuery.trim().toLowerCase();
  const filteredSelectablePractices = normalizedOrgSearchQuery
    ? selectablePractices.filter((practice) =>
        (practice.PracticeName || "").toLowerCase().includes(normalizedOrgSearchQuery),
      )
    : selectablePractices;

  const handlePracticeSelect = (practiceId) => {
    handlePracticeChange({ target: { value: practiceId } });
    setIsOrgMenuOpen(false);
  };

  return {
    orgMenuRef,
    isOrgMenuOpen,
    setIsOrgMenuOpen,
    showOrganizationModal,
    setShowOrganizationModal,
    organizationDraftName,
    setOrganizationDraftName,
    organizationUpdateError,
    isSavingOrganizationName,
    openOrganizationModal,
    handleOrganizationRename,
    handlePracticeChange,
    handlePracticeSelect,
    selectablePractices,
    filteredSelectablePractices,
    orgSearchQuery,
    setOrgSearchQuery,
    selectedPracticeLabel,
  };
};
